import React from 'react';
import { useAuth } from '../contexts/useAuth';
import {
  DEFAULT_COMPLIANCE_CATEGORIES,
  subscribeComplianceCategories,
  type ComplianceCategory,
} from '../lib/complianceCategories';
import { categorizeFirestoreError } from '../lib/firebaseErrors';
import { roleIndicatesSupervisorNurse, type RoleContext } from '../lib/roles';
import { canViewOrgClimateSurveys } from '../lib/orgClimateSurveys/access';

export const NURSING_NOTE_IDENTIFIERS = new Set(['nurse_notes', 'บันทึกทางการพยาบาล']);

export const ETHIC_IDENTIFIERS = new Set(['ethicSurveys', 'บรรยากาศองค์กรจริยธรรม']);

export const ORG_CLIMATE_IDENTIFIERS = new Set([
  'orgClimateSurveys',
  'แบบประเมินบรรยากาศจริยธรรมในองค์การพยาบาล',
]);

export const MED_ERROR_INCIDENT_IDENTIFIERS = new Set(['med_error_incident', 'รายงานอุบัติการณ์ความคลาดเคลื่อนทางยา']);

export const FALL_INCIDENT_IDENTIFIERS = new Set(['fall_incident', 'จำนวนอุบัติการณ์การพลัดตกหกล้ม']);

/** KPI / normal ComplianceHub: HWN and SN see ethic + incident categories under LOF scope (not only legacy HWN || SUPERVISOR+SN). */
function seesEthicMedFallOnNormalPage(roleContext: RoleContext | null): boolean {
  if (!roleContext) return false;
  const p = roleContext.positionRole?.toUpperCase().replace(/-/g, '_') ?? '';
  const g = roleContext.generalRole?.toUpperCase() ?? '';
  if (p === 'HWN' || p === 'HEAD_WARD_NURSE' || g.includes('HEAD_WARD')) return true;
  if ((roleContext.roles ?? []).some((r) => typeof r === 'string' && ['HWN', 'HEAD_WARD_NURSE'].includes(r.trim().toUpperCase()))) {
    return true;
  }
  if (roleIndicatesSupervisorNurse(roleContext.positionRole)) return true;
  if (roleIndicatesSupervisorNurse(roleContext.generalRole)) return true;
  return (roleContext.roles ?? []).some((r) => typeof r === 'string' && roleIndicatesSupervisorNurse(r));
}

/**
 * Category visibility rules:
 * - Admin: all categories (non-nursing categories are forced unavailable for now)
 * - Non-admin:
 *   - If user has teamCategoryIds -> allow those category ids
 *   - Else if user has teamIds -> allow categories where category.teamId is in teamIds
 *   - Else -> fallback to nursing note category only
 */
export const filterAllowedCategories = (
  categories: ComplianceCategory[],
  isAdmin: boolean,
  teamIds: string[],
  teamCategoryIds: string[],
  allowedCategoryIds: string[],
): ComplianceCategory[] => {
  const teamCategorySet = new Set(teamCategoryIds.filter(Boolean));
  const teamIdSet = new Set(teamIds.filter(Boolean));
  const allowedCategorySet = new Set(allowedCategoryIds.filter(Boolean));

  const baseCategories = isAdmin ? categories : categories;

  const byAllowedCategory = allowedCategorySet.size
    ? categories.filter((c) => allowedCategorySet.has(c.id))
    : [];

  const byTeamCategory = !isAdmin && !byAllowedCategory.length && teamCategorySet.size
    ? categories.filter((c) => teamCategorySet.has(c.id))
    : [];

  const byTeamId = !isAdmin && !byAllowedCategory.length && !byTeamCategory.length && teamIdSet.size
    ? categories.filter((c) => !!c.teamId && teamIdSet.has(c.teamId))
    : [];

  const fallback = !isAdmin && !byAllowedCategory.length && !byTeamCategory.length && !byTeamId.length
    ? categories.filter((c) => NURSING_NOTE_IDENTIFIERS.has(c.id) || NURSING_NOTE_IDENTIFIERS.has(c.title))
    : [];

  const picked = isAdmin
    ? baseCategories
    : byAllowedCategory.length
      ? byAllowedCategory
      : byTeamCategory.length
        ? byTeamCategory
        : byTeamId.length
          ? byTeamId
          : fallback;

  // Ensure nursing note exists (some screens expect it).
  if (!byAllowedCategory.length) {
    const hasNursingNote = picked.some((c) => NURSING_NOTE_IDENTIFIERS.has(c.id) || NURSING_NOTE_IDENTIFIERS.has(c.title));
    if (!hasNursingNote) {
      const nursing =
        categories.find((c) => NURSING_NOTE_IDENTIFIERS.has(c.id) || NURSING_NOTE_IDENTIFIERS.has(c.title)) ??
        DEFAULT_COMPLIANCE_CATEGORIES.find((c) => NURSING_NOTE_IDENTIFIERS.has(c.id) || NURSING_NOTE_IDENTIFIERS.has(c.title));
      if (nursing) picked.push({ ...nursing, active: nursing.active ?? true });
    }
  }

  const hasMedErrorIncident = picked.some((c) => MED_ERROR_INCIDENT_IDENTIFIERS.has(c.id) || MED_ERROR_INCIDENT_IDENTIFIERS.has(c.title));
  if (!hasMedErrorIncident && teamCategoryIds.some((id) => id === 'med_error_incident')) {
    const medErrorIncident =
      categories.find((c) => MED_ERROR_INCIDENT_IDENTIFIERS.has(c.id) || MED_ERROR_INCIDENT_IDENTIFIERS.has(c.title)) ??
      DEFAULT_COMPLIANCE_CATEGORIES.find((c) => MED_ERROR_INCIDENT_IDENTIFIERS.has(c.id) || MED_ERROR_INCIDENT_IDENTIFIERS.has(c.title));
    if (medErrorIncident) picked.push({ ...medErrorIncident, active: medErrorIncident.active ?? true });
  }

  const hasFallIncident = picked.some((c) => FALL_INCIDENT_IDENTIFIERS.has(c.id) || FALL_INCIDENT_IDENTIFIERS.has(c.title));
  if (!hasFallIncident && teamCategoryIds.some((id) => id === 'fall_incident')) {
    const fallIncident =
      categories.find((c) => FALL_INCIDENT_IDENTIFIERS.has(c.id) || FALL_INCIDENT_IDENTIFIERS.has(c.title)) ??
      DEFAULT_COMPLIANCE_CATEGORIES.find((c) => FALL_INCIDENT_IDENTIFIERS.has(c.id) || FALL_INCIDENT_IDENTIFIERS.has(c.title));
    if (fallIncident) picked.push({ ...fallIncident, active: fallIncident.active ?? true });
  }

  const normalized = picked.map((c) => ({ ...c, active: c.active ?? true }));
  const enforced = normalized.map((c) => ({
    ...c,
    active:
      NURSING_NOTE_IDENTIFIERS.has(c.id) ||
      NURSING_NOTE_IDENTIFIERS.has(c.title) ||
      ETHIC_IDENTIFIERS.has(c.id) ||
      ETHIC_IDENTIFIERS.has(c.title) ||
      MED_ERROR_INCIDENT_IDENTIFIERS.has(c.id) ||
      MED_ERROR_INCIDENT_IDENTIFIERS.has(c.title) ||
      FALL_INCIDENT_IDENTIFIERS.has(c.id) ||
      FALL_INCIDENT_IDENTIFIERS.has(c.title) ||
      ORG_CLIMATE_IDENTIFIERS.has(c.id) ||
      ORG_CLIMATE_IDENTIFIERS.has(c.title)
        ? true
        : false,
  }));

  return enforced;
};

export const useComplianceCategories = () => {
  const { isAdmin, loading: authLoading, firestoreUser, roleContext, authUser } = useAuth();

  const teamIds = React.useMemo(() => (Array.isArray((firestoreUser as any)?.teamIds) ? ((firestoreUser as any).teamIds as string[]) : []), [firestoreUser]);
  const allowedCategoryIds = React.useMemo(
    () =>
      Array.from(
        new Set([
          ...(Array.isArray(roleContext?.allowedCategoryIds) ? roleContext.allowedCategoryIds : []),
          ...(Array.isArray(roleContext?.teamAllowedCategoryIds) ? roleContext.teamAllowedCategoryIds : []),
        ]),
      ).filter(Boolean),
    [roleContext?.allowedCategoryIds, roleContext?.teamAllowedCategoryIds],
  );
  const teamCategoryIds = React.useMemo(
    () =>
      Array.from(
        new Set([
          ...(Array.isArray((firestoreUser as any)?.teamCategoryIds) ? (firestoreUser as any).teamCategoryIds : []),
          ...allowedCategoryIds,
        ]),
      ).filter(Boolean),
    [firestoreUser, allowedCategoryIds],
  );

  const [categories, setCategories] = React.useState<ComplianceCategory[]>(() =>
    filterAllowedCategories(DEFAULT_COMPLIANCE_CATEGORIES, isAdmin, teamIds, teamCategoryIds, allowedCategoryIds),
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const applyCategoryFilter = React.useCallback(
    (nextCategories: ComplianceCategory[]) => {
      let result = filterAllowedCategories(nextCategories, isAdmin, teamIds, teamCategoryIds, allowedCategoryIds);
      // HWN/SN see ethic on normal page (dept/LOF scope). Ethic team page requires ETHIC team role.
      const isHWNOrSN = seesEthicMedFallOnNormalPage(roleContext ?? null);
      if (!isAdmin && isHWNOrSN && !result.some((c) => c.id === 'ethicSurveys')) {
        const ethicCat =
          nextCategories.find((c) => c.id === 'ethicSurveys') ??
          DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'ethicSurveys');
        if (ethicCat) result = [...result, { ...ethicCat, active: true }];
      }
      if (!isAdmin && isHWNOrSN && !result.some((c) => c.id === 'med_error_incident')) {
        const medErrorIncidentCat =
          nextCategories.find((c) => c.id === 'med_error_incident') ??
          DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'med_error_incident');
        if (medErrorIncidentCat) result = [...result, { ...medErrorIncidentCat, active: true }];
      }
      if (!isAdmin && isHWNOrSN && !result.some((c) => c.id === 'fall_incident')) {
        const fallCat =
          nextCategories.find((c) => c.id === 'fall_incident') ??
          DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'fall_incident');
        if (fallCat) result = [...result, { ...fallCat, active: true }];
      }
      if (canViewOrgClimateSurveys(authUser?.uid)) {
        const orgClimateCat =
          nextCategories.find((c) => c.id === 'orgClimateSurveys') ??
          DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'orgClimateSurveys');
        if (orgClimateCat) {
          const hasOrgClimate = result.some((c) => c.id === 'orgClimateSurveys');
          if (!hasOrgClimate) {
            result = [...result, { ...orgClimateCat, active: true }];
          } else {
            result = result.map((c) =>
              c.id === 'orgClimateSurveys' ? { ...c, active: true } : c,
            );
          }
        }
      } else {
        result = result.filter(
          (c) => !ORG_CLIMATE_IDENTIFIERS.has(c.id) && !ORG_CLIMATE_IDENTIFIERS.has(c.title),
        );
      }
      return result;
    },
    [isAdmin, teamIds, teamCategoryIds, allowedCategoryIds, roleContext, authUser?.uid],
  );

  React.useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    setError(null);

    let hasLoaded = false;

    const unsubscribe = subscribeComplianceCategories(
      (data) => {
        setCategories(applyCategoryFilter(data));
        if (!hasLoaded) {
          setLoading(false);
          hasLoaded = true;
        }
      },
      (err) => {
        console.error('Failed to load compliance categories', err);
        const category = categorizeFirestoreError(err);
        setError(category === 'permission-denied' ? 'You do not have permission to view categories.' : 'Failed to load categories.');
        setCategories(applyCategoryFilter(DEFAULT_COMPLIANCE_CATEGORIES));
        if (!hasLoaded) {
          setLoading(false);
          hasLoaded = true;
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [authLoading, applyCategoryFilter]);

  return { categories, loading, error };
};
